import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { color } from '../../app/theme';
import { GATriggerType, UrlFilterType } from '../../app/types';
import { changeSeconds } from '../../app/utils';
import Tooltip from '../shared/Tooltip';

const useStyles = makeStyles(
  {
    root: {
      paddingBottom: 24,
    },
    line: {
      flexGrow: 1,
      borderBottom: `1px solid ${color.$9}`,
    },
    text: {
      margin: '0 16px',
      maxWidth: '80%',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      letterSpacing: 'normal',
      color: color.$11,
    },
    order: { display: 'flex', alignItems: 'center' },
    condition: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    gaView: {
      fontWeight: '400',
      fontSize: '13px',
      lineHeight: '18px',
      color: color.$11,
      display: 'inline-flex',
      alignItems: 'center',
    },
    url: {
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: '1',
      display: '-webkit-box',
      overflow: 'hidden',
      wordBreak: 'break-all',
      color: color.$1,
      textDecoration: 'underline',
      cursor: 'pointer',
    },
    urlText: {
      color: color.$13,
      textDecoration: 'none',
      cursor: 'default',
    },
    urlType: {
      display: 'inline-flex',
      wordBreak: 'keep-all',
    },
  },
  {
    name: 'PreviewGAConditionNode',
  },
);

const PreviewGAConditionNode = ({ node }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const nodes = useSelector(store => store.ma.data?.nodes) || [];
  const { type, urlType, url, pageViewTimes, staySec, groupId } =
    node?.data || {};

  const groupNode = useMemo(
    () => nodes.find(n => n?.id === groupId) || {},
    [nodes],
  );
  const { triggerType, timeType, time } = groupNode?.data || {};

  const timeText = useMemo(() => {
    if (triggerType === GATriggerType.GAVIEW) {
      return t(`GAConditionNode.${GATriggerType.GAVIEW}`, {
        view: pageViewTimes || 0,
      });
    }
    return (
      t(`GAConditionNode.${timeType}`, {
        time: changeSeconds(time, timeType),
      }) + t(`GAConditionNode.${type}.${triggerType}`)
    );
  }, [timeType, time, pageViewTimes]);

  const staySecText = useMemo(() => {
    const minute = changeSeconds(staySec, 'minute');
    const second = changeSeconds(staySec, 'second');

    if (staySec === null || (!second && !minute)) {
      return '';
    }

    return ` ${minute ? `${t('message-node.minute', { minute })}` : ''}${
      second ? ` ${t('message-node.second', { second })}` : ''
    }`;
  }, [staySec]);

  return (
    <div className={classes.root}>
      <div className={classes.order}>
        <span className={classes.line} />
        <span className={classes.text}>
          <div className={classes.condition}>{timeText}</div>
        </span>
        <span className={classes.line} />
      </div>
      {triggerType === GATriggerType.GAVIEW && (
        <div style={{ marginTop: 24, textAlign: 'center' }}>
          {url && (
            <div className={classes.gaView}>
              <div className={classes.urlType}>{`${t(`GA.${urlType}`)}`}</div>：
              {UrlFilterType.EQ === urlType ? (
                <a
                  className={classes.url}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {url}
                </a>
              ) : (
                <Tooltip light placement="top" title={url}>
                  <span className={classnames(classes.url, classes.urlText)}>
                    {url}
                  </span>
                </Tooltip>
              )}
            </div>
          )}
          {staySec && (
            <div className={classes.gaView}>
              {t(`GA.stay1`)}
              {staySecText}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PreviewGAConditionNode;
